import { stripMioInternalAnnotations } from '@mio/shared';
import { Link } from '@tanstack/react-router';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { safeSourceUrl } from '../render-safety.service';

import { MioCodeBlock } from './mio-code-block';

// Stable component identities keep code controls and scroll positions intact as text streams.
const components: Components = {
  pre: ({ node, children }) => {
    const code = node?.children[0];
    if (code?.type !== 'element' || code.tagName !== 'code') return <pre>{children}</pre>;
    const source = code.children
      .map((child) => (child.type === 'text' ? child.value : ''))
      .join('');
    const classes = code.properties.className;
    const languageClass = Array.isArray(classes)
      ? classes.find((value) => typeof value === 'string' && value.startsWith('language-'))
      : undefined;
    // Markdown adds a final newline to code nodes; don't display it as an extra empty line.
    return (
      <MioCodeBlock
        code={source.replace(/\n$/, '')}
        language={typeof languageClass === 'string' ? languageClass.slice(9) : undefined}
      />
    );
  },
  a: ({ children, href }) => {
    const safe = href ? safeSourceUrl(href) : undefined;
    return safe?.startsWith('/') ? (
      <Link to={safe}>{children}</Link>
    ) : safe ? (
      <a href={safe} target={safe.startsWith('/') ? undefined : '_blank'} rel="noopener noreferrer">
        {children}
      </a>
    ) : (
      <span>{children}</span>
    );
  },
  img: ({ alt }) => <span>{alt}</span>,
  table: ({ children }) => (
    <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Mio table">
      <table>{children}</table>
    </div>
  ),
};

/** Mio's replies, rendered with chat-scale typography. */
export function MioMarkdown({ source }: { source: string }) {
  return (
    <div className="prose-mio-chat">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {stripMioInternalAnnotations(source)}
      </ReactMarkdown>
    </div>
  );
}
